import type { RepoItem } from "@/components/features/matter/file-repo/FileRepository";
import { FolderTree } from "@/components/features/matter/file-repo/FolderTree";
import { Typography } from "@/components/ui";
import type { MassTort, Matter, MatterFile } from "@/lib/types";
import { buildMatterFileFolderBreadcrumbs } from "@/lib/utils/matterFile";
import { cn } from "@/lib/utils/cn";
import { useMemo } from "react";

type DestinationFolderPickerBaseProps = {
  selectedFolderId: MatterFile["id"];
  setSelectedFolderId: (folderId: MatterFile["id"]) => void;
  title?: string;
  description?: string;
  className?: string;
};

type DestinationFolderPickerProps = DestinationFolderPickerBaseProps &
  (
    | {
        matter: Matter;
        massTort?: never;
      }
    | {
        massTort: MassTort;
        matter?: never;
      }
  );

export const DestinationFolderPicker = ({
  matter,
  massTort,
  selectedFolderId,
  setSelectedFolderId,
  title = "Destination folder",
  description = "Choose where to save the signed document",
  className,
}: DestinationFolderPickerProps) => {
  const matterFiles = useMemo(
    () => matter?.files ?? massTort?.files ?? [],
    [massTort?.files, matter?.files],
  );
  const rootLabel = matter ? "Matter Files" : "Mass Tort Files";
  const fileParentId = matter?.id ?? massTort?.id ?? "";
  const folderItems = useMemo(
    () =>
      matterFiles
        .filter((item) => item.isFolder)
        .map(
          (item): RepoItem =>
            ({
              ...item,
              matterId: item.matterId ?? fileParentId,
              kind: "regular",
            }) as RepoItem,
        ),
    [fileParentId, matterFiles],
  );

  const selectionDescription = useMemo(() => {
    if (!selectedFolderId) {
      return `${rootLabel} (root)`;
    }

    const crumbs = buildMatterFileFolderBreadcrumbs(
      matterFiles.map((file) => ({
        id: file.id,
        idLocation: file.idLocation ?? "",
        isFolder: file.isFolder,
        name: file.name,
      })),
      selectedFolderId,
    );
    if (crumbs.length === 0) {
      return `${rootLabel} (root)`;
    }

    return [rootLabel, ...crumbs.map((c) => c.name)].join(" / ");
  }, [matterFiles, rootLabel, selectedFolderId]);

  return (
    <div
      className={cn(
        "border-border/80 bg-card flex h-full min-h-0 flex-col border-l",
        className,
      )}
    >
      <div className="p-4 pb-3">
        <Typography className="text-xl font-semibold">{title}</Typography>
        <Typography className="text-muted-foreground mt-1 text-xs">
          {description}
        </Typography>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 px-3.5 pb-3.5">
        <div className="bg-muted/20 min-w-0 rounded-xl border px-4 py-3">
          <Typography className="text-muted-foreground text-xs font-medium">
            Selected destination
          </Typography>
          <Typography className="mt-1 max-w-full min-w-0 truncate text-sm font-medium break-normal whitespace-nowrap">
            {selectionDescription}
          </Typography>
        </div>

        <div className="min-h-0 flex-1 overflow-auto rounded-xl border p-2">
          <FolderTree
            matter={{ matterTitle: rootLabel }}
            items={folderItems}
            onSelectItem={setSelectedFolderId}
            selectedItemId={selectedFolderId || null}
            onMoveItem={() => false}
            enableDrag={false}
            currentFolderId={selectedFolderId}
            showCheckboxes={false}
          />
        </div>
      </div>
    </div>
  );
};
